/**
 * B1064 — Tendance de latence sur 30 jours (Eric 2026-09-04 SPEC STRICK PROD).
 *
 * Les alertes disent « ça vient de casser ». Elles ne montrent jamais la
 * dégradation lente : une connexion qui passe de 800 ms à 2,4 s en trois
 * semaines ne déclenche rien et finit en panne un lundi matin.
 *
 * Trois courbes par jour (choix Eric) : médiane, p95 et pire cas. La ligne
 * pointillée est le seuil d'alerte de lenteur en vigueur — on voit d'un coup
 * d'œil à quelle distance on en est.
 */
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const dayLabel = (day) => {
  if (!day) return "";
  const [, m, d] = String(day).split("-");
  return `${d}/${m}`;
};

function TrendTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div
      className="rounded-lg border border-zinc-200 bg-white px-3 py-2 text-xs shadow-lg"
      data-testid="latency-trend-tooltip"
    >
      <div className="font-semibold text-zinc-900">{label}</div>
      {p.runs ? (
        <>
          <div className="text-zinc-700">Médiane : {p.p50_ms} ms</div>
          <div className="text-zinc-700">p95 : {p.p95_ms} ms</div>
          <div className="text-zinc-700">Pire cas : {p.worst_ms} ms</div>
          <div className="mt-1 text-zinc-500">
            {p.ok}/{p.runs} passages · disponibilité {p.uptime_pct ?? "—"} %
          </div>
        </>
      ) : (
        <div className="text-zinc-500">Aucun passage ce jour-là</div>
      )}
    </div>
  );
}

export const LatencyTrendChart = ({ trend }) => {
  const points = (trend?.points || []).map((p) => ({ ...p, jour: dayLabel(p.day) }));
  const measured = points.filter((p) => p.runs);
  const hasData = measured.length > 0;
  // Le seuil doit rester visible même quand la latence en est très loin,
  // sinon on ne voit pas la marge dont on dispose. Et avec une ou deux
  // journées mesurées, une ligne sans point serait invisible.
  const ceiling = Math.max(
    trend?.slow_threshold_ms || 0,
    ...measured.map((p) => p.worst_ms || 0),
    1000
  );
  const showDots = measured.length <= 3;

  return (
    <section
      className="rounded-xl border border-zinc-200 bg-white p-5"
      data-testid="latency-trend-section"
    >
      <h2 className="text-base font-semibold text-zinc-900">
        Tendance de la latence — {trend?.days ?? 30} derniers jours
      </h2>
      <p className="text-xs text-zinc-500 mt-0.5 mb-4 max-w-3xl">
        Une alerte prévient d'une casse. Elle ne montre jamais une dégradation lente :
        passer de 800 ms à 2,4 s en trois semaines ne déclenche rien et finit en panne
        un lundi matin. Médiane, p95 et pire cas par journée, avec le seuil d'alerte de
        lenteur en pointillé.
      </p>

      {!hasData ? (
        <p
          className="text-sm text-zinc-600 bg-zinc-50 border border-zinc-200 rounded-lg px-3 py-2"
          data-testid="latency-trend-empty"
        >
          Aucune mesure encore agrégée. La courbe se remplit à chaque passage de la sonde
          (toutes les 5 minutes).
        </p>
      ) : (
        <>
          <div className="h-64 w-full min-w-0" data-testid="latency-trend-chart">
            <ResponsiveContainer width="100%" height="100%" minHeight={256}>
              <LineChart data={points} margin={{ top: 5, right: 12, bottom: 0, left: -12 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" vertical={false} />
                <XAxis dataKey="jour" tick={{ fontSize: 11, fill: "#71717a" }} interval="preserveStartEnd" />
                <YAxis
                  tick={{ fontSize: 11, fill: "#71717a" }}
                  unit=" ms"
                  width={64}
                  domain={[0, Math.round(ceiling * 1.1)]}
                  allowDataOverflow={false}
                />
                <Tooltip content={<TrendTooltip />} />
                {trend?.slow_threshold_ms ? (
                  <ReferenceLine
                    y={trend.slow_threshold_ms}
                    stroke="#dc2626"
                    strokeDasharray="6 4"
                    label={{
                      value: `seuil ${(trend.slow_threshold_ms / 1000).toFixed(1)} s`,
                      position: "insideTopRight",
                      fontSize: 11,
                      fill: "#dc2626",
                    }}
                  />
                ) : null}
                <Line type="monotone" dataKey="worst_ms" name="Pire cas" stroke="#f59e0b"
                      strokeWidth={1.5} dot={showDots} connectNulls />
                <Line type="monotone" dataKey="p95_ms" name="p95" stroke="#7c3aed"
                      strokeWidth={1.5} dot={showDots} connectNulls />
                <Line type="monotone" dataKey="p50_ms" name="Médiane" stroke="#002FA7"
                      strokeWidth={2.5} dot={showDots} connectNulls />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-xs text-zinc-600"
               data-testid="latency-trend-legend">
            <span className="inline-flex items-center gap-1.5">
              <span className="w-4 h-0.5 bg-[#002FA7]" /> Médiane
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="w-4 h-0.5 bg-[#7c3aed]" /> p95
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="w-4 h-0.5 bg-amber-500" /> Pire cas
            </span>
            <span data-testid="latency-trend-summary">
              {trend.days_with_data} jour(s) mesuré(s) · {trend.total_runs} passages ·{" "}
              {trend.total_failed} échec(s) · médiane des médianes{" "}
              {trend.median_of_medians_ms ?? "—"} ms · pire cas {trend.worst_ms ?? "—"} ms
            </span>
          </div>
          <p className="text-xs text-zinc-400 mt-2">
            Les passages détaillés sont purgés après {trend.run_retention_days} jours ; un
            résumé quotidien est conservé pour que la tendance ne disparaisse jamais avec eux.
          </p>
        </>
      )}
    </section>
  );
};

export default LatencyTrendChart;
